import type { Shape } from "plotly.js-dist-min";

import type { DataFrameDto } from "@/components/datascience/DataFrameWidget.vue";
import { type PlotlyWidgetSpec } from "@/components/datascience/PlotlyWidget.vue";

export const MAX_LABEL_LENGTH = 20;

export interface InspectionDataframe extends DataFrameDto {
  method: string;
  verboseMethodName: string;
  datasource?: string;
  traceName?: string;
  pointSplitIndices?: number[][];
}

export interface InspectionPlotDecoration {
  plotTitle: string;
  xAxisLabel: string;
  tracePrefix: string;
}

export interface InspectionPlotOptions extends InspectionPlotDecoration {
  dataframes: InspectionDataframe[];
  legendTitle?: string;
}

function sortCoefficientFeatureNames(featureNames: string[]): string[] {
  const sorted = [...featureNames].sort((a, b) => {
    if (a === "Intercept") return -1;
    if (b === "Intercept") return 1;
    const na = a.match(/^.*#(\d+)$/)?.[1];
    const nb = b.match(/^.*#(\d+)$/)?.[1];
    if (na !== undefined && nb !== undefined) return parseInt(na, 10) - parseInt(nb, 10);
    return a.localeCompare(b);
  });
  return sorted;
}

export function truncateLabel(label: string, maxLength: number = MAX_LABEL_LENGTH): string {
  if (label.length <= maxLength) return label;
  return label.slice(0, maxLength - 1) + "…";
}

export function normalizeMdiDataframe(df: InspectionDataframe): InspectionDataframe {
  if (df.method !== "impurity_decrease") return df;
  const cols = df.columns as string[] | undefined;
  const data = df.data as unknown[][] | undefined;
  const idxFeature = cols?.indexOf("feature") ?? -1;
  const idxImportance = cols?.indexOf("importance") ?? -1;
  const idxSplit = cols?.indexOf("split") ?? -1;
  if (
    !Array.isArray(cols) ||
    !Array.isArray(data) ||
    data.length === 0 ||
    idxFeature === -1 ||
    idxImportance === -1
  ) {
    return df;
  }

  const hasSplit = idxSplit >= 0;

  if (hasSplit) {
    const byFeature = new Map<string, number[]>();
    for (const row of data) {
      const feature = String((row as unknown[])[idxFeature]);
      const importance = Number((row as unknown[])[idxImportance]);
      const vals = byFeature.get(feature) ?? [];
      vals.push(importance);
      byFeature.set(feature, vals);
    }
    const featureNames = Array.from(byFeature.keys()).sort((a, b) => {
      const na = a.match(/^.*#(\d+)$/)?.[1];
      const nb = b.match(/^.*#(\d+)$/)?.[1];
      if (na !== undefined && nb !== undefined) return parseInt(na, 10) - parseInt(nb, 10);
      return a.localeCompare(b);
    });
    return {
      ...df,
      index: featureNames,
      data: featureNames.map((f) => byFeature.get(f)!),
    };
  }

  return {
    ...df,
    index: data.map((row: unknown[]) => (row as unknown[])[idxFeature]),
    data: data.map((row: unknown[]) => [(row as unknown[])[idxImportance]]),
  };
}

export function isMdiBoxFormat(df: InspectionDataframe): boolean {
  if (df.method !== "impurity_decrease" || !Array.isArray(df.data)) return false;
  return df.data.some((row) => Array.isArray(row) && row.length > 1);
}

export function isCoefficientsBoxFormat(dataframes: InspectionDataframe[]): boolean {
  return dataframes.some(
    (df) => Array.isArray(df.data) && df.data.some((row) => Array.isArray(row) && row.length > 1)
  );
}

export function normalizePermutationImportanceDataframe(
  df: InspectionDataframe,
  mlTask: string,
  dataSourceName: string
): InspectionDataframe {
  if (df.method !== "permutation_importance" || !dataSourceName) return df;
  const cols = df.columns as string[];
  const data = df.data as unknown[][];
  const idx = {
    data_source: cols.indexOf("data_source"),
    metric: cols.indexOf("metric"),
    feature: cols.indexOf("feature"),
    value: cols.indexOf("value"),
    split: cols.indexOf("split"),
  };

  if (idx.data_source === -1 || idx.metric === -1 || idx.feature === -1 || idx.value === -1)
    return df;

  const chosenMetric = mlTask.includes("classification") ? "accuracy" : "r2";
  const hasSplit = idx.split >= 0;
  const byFeature = new Map<string, number[]>();
  const byFeatureSplits = hasSplit ? new Map<string, number[]>() : null;

  for (const row of data) {
    if (row[idx.data_source] !== dataSourceName || row[idx.metric] !== chosenMetric) continue;
    const feature = String(row[idx.feature]);
    const value = Number(row[idx.value]);
    const vals = byFeature.get(feature) ?? [];
    vals.push(value);
    byFeature.set(feature, vals);
    if (byFeatureSplits) {
      const splits = byFeatureSplits.get(feature) ?? [];
      splits.push(Number(row[idx.split]));
      byFeatureSplits.set(feature, splits);
    }
  }

  const featureNames = Array.from(byFeature.keys()).sort((a, b) => {
    const na = a.match(/^.*#(\d+)$/)?.[1];
    const nb = b.match(/^.*#(\d+)$/)?.[1];
    if (na !== undefined && nb !== undefined) return parseInt(na, 10) - parseInt(nb, 10);
    return a.localeCompare(b);
  });

  const result: InspectionDataframe = {
    ...df,
    index: featureNames,
    data: featureNames.map((f) => byFeature.get(f)!),
  };
  if (byFeatureSplits) {
    result.pointSplitIndices = featureNames.map((f) => byFeatureSplits.get(f)!);
  }
  return result;
}

export function normalizeCoefficientsDataframe(
  df: InspectionDataframe
): InspectionDataframe | { dataframes: InspectionDataframe[]; legendTitle: "Label" | "Output" } {
  if (df.method !== "coefficients") return df;
  const cols = df.columns as string[];
  const data = df.data as unknown[][];
  const idxFeature = cols.indexOf("feature");
  const idxCoeff = cols.indexOf("coefficient");
  const idxLabel = cols.indexOf("label");
  const idxOutput = cols.indexOf("output");
  const idxSplit = cols.indexOf("split");

  if (idxFeature === -1 || idxCoeff === -1) return df;

  const groupCol = idxLabel >= 0 ? "label" : idxOutput >= 0 ? "output" : null;
  const groupIdx = groupCol === "label" ? idxLabel : groupCol === "output" ? idxOutput : -1;
  const hasSplit = idxSplit >= 0;

  if (groupIdx === -1) {
    if (hasSplit) {
      const byFeature = new Map<string, number[]>();
      for (const row of data) {
        const feature = String(row[idxFeature]);
        const value = Number(row[idxCoeff]);
        const vals = byFeature.get(feature) ?? [];
        vals.push(value);
        byFeature.set(feature, vals);
      }
      const featureNames = sortCoefficientFeatureNames(Array.from(byFeature.keys()));
      return {
        ...df,
        index: featureNames,
        data: featureNames.map((f) => byFeature.get(f)!),
      };
    }
    const byFeature = new Map<string, number>();
    for (const row of data) {
      byFeature.set(String(row[idxFeature]), Number(row[idxCoeff]));
    }
    const featureNames = sortCoefficientFeatureNames(Array.from(byFeature.keys()));
    return {
      ...df,
      index: featureNames,
      data: featureNames.map((f) => [byFeature.get(f)!]),
    };
  }

  if (hasSplit) {
    const groups = new Map<number, Map<string, number[]>>();
    for (const row of data) {
      const groupKey = Number(row[groupIdx]);
      const feature = String(row[idxFeature]);
      const value = Number(row[idxCoeff]);
      if (!groups.has(groupKey)) groups.set(groupKey, new Map());
      const map = groups.get(groupKey)!;
      const vals = map.get(feature) ?? [];
      vals.push(value);
      map.set(feature, vals);
    }
    const allFeatures = new Set([...groups.values()].flatMap((m) => [...m.keys()]));
    const featureNames = sortCoefficientFeatureNames(Array.from(allFeatures));
    const groupKeys = Array.from(groups.keys()).sort((a, b) => a - b);
    const legendTitle = groupCol === "label" ? "Label" : "Output";
    const dataframes = groupKeys.map((key) => ({
      ...df,
      index: featureNames,
      data: featureNames.map((f) => groups.get(key)!.get(f) ?? []),
      traceName: String(key),
    }));
    return { dataframes, legendTitle };
  }

  const groups = new Map<number, Map<string, number>>();
  for (const row of data) {
    const groupKey = Number(row[groupIdx]);
    const feature = String(row[idxFeature]);
    const value = Number(row[idxCoeff]);
    if (!groups.has(groupKey)) groups.set(groupKey, new Map());
    groups.get(groupKey)!.set(feature, value);
  }

  const allFeatures = new Set([...groups.values()].flatMap((m) => [...m.keys()]));

  const featureNames = sortCoefficientFeatureNames(Array.from(allFeatures));
  const groupKeys = Array.from(groups.keys()).sort((a, b) => a - b);
  const legendTitle = groupCol === "label" ? "Label" : "Output";
  const dataframes = groupKeys.map((key) => ({
    ...df,
    index: featureNames,
    data: featureNames.map((f) => [groups.get(key)!.get(f) ?? 0]),
    traceName: String(key),
  }));

  return { dataframes, legendTitle };
}

export function createInspectionPlotDecoration(
  method: string,
  options: {
    reportLabel: string;
    datasetLabel: string;
    mlTask?: string;
    tracePrefix?: string;
  }
): InspectionPlotDecoration {
  const { reportLabel, datasetLabel, mlTask = "", tracePrefix = "" } = options;
  switch (method) {
    case "permutation_importance": {
      let xAxisLabel: string;
      if (mlTask.includes("classification")) {
        xAxisLabel = "Decrease in accuracy";
      } else if (mlTask.includes("regression")) {
        xAxisLabel = "Decrease in R²";
      } else {
        xAxisLabel = "Score decrease";
      }
      return {
        plotTitle: `Permutation importance (${datasetLabel}) · ${reportLabel}`,
        xAxisLabel,
        tracePrefix,
      };
    }
    case "impurity_decrease":
      return {
        plotTitle: `Mean decrease in impurity (${datasetLabel}) · ${reportLabel}`,
        xAxisLabel: "Mean decrease impurity",
        tracePrefix,
      };
    case "coefficients":
      return {
        plotTitle: `Model coefficients · ${reportLabel}`,
        xAxisLabel: "Model coefficient value",
        tracePrefix,
      };
    default:
      return {
        plotTitle: "",
        xAxisLabel: "Model coefficient value",
        tracePrefix,
      };
  }
}

/** Matplotlib Tab10 qualitative palette (10 colors). */
const TAB10 = [
  "rgb(31, 119, 180)",
  "rgb(255, 127, 14)",
  "rgb(44, 160, 44)",
  "rgb(214, 39, 40)",
  "rgb(148, 103, 189)",
  "rgb(140, 86, 75)",
  "rgb(227, 119, 194)",
  "rgb(127, 127, 127)",
  "rgb(188, 189, 34)",
  "rgb(23, 190, 207)",
];

/** Matplotlib Tab20 qualitative palette (20 colors). */
const TAB20 = [
  "rgb(31, 119, 180)",
  "rgb(174, 199, 232)",
  "rgb(255, 127, 14)",
  "rgb(255, 187, 120)",
  "rgb(44, 160, 44)",
  "rgb(152, 223, 138)",
  "rgb(214, 39, 40)",
  "rgb(255, 152, 150)",
  "rgb(148, 103, 189)",
  "rgb(197, 176, 213)",
  "rgb(140, 86, 75)",
  "rgb(196, 156, 148)",
  "rgb(227, 119, 194)",
  "rgb(247, 182, 210)",
  "rgb(127, 127, 127)",
  "rgb(199, 199, 199)",
  "rgb(188, 189, 34)",
  "rgb(219, 219, 141)",
  "rgb(23, 190, 207)",
  "rgb(158, 218, 229)",
];

const SPLIT_COLORAXIS_ID = "coloraxis2";

function getSplitPaletteColors(n: number): string[] {
  const palette = n <= 10 ? TAB10 : TAB20;
  return Array.from({ length: n }, (_, i) => palette[i % palette.length] ?? palette[0]!);
}

function makeDiscreteColorscale(n: number): [number, string][] {
  const colors = getSplitPaletteColors(n);
  const scale: [number, string][] = [];
  for (let i = 0; i < n; i++) {
    const t0 = n > 1 ? i / (n - 1) : 0;
    const t1 = n > 1 ? Math.min((i + 1) / (n - 1), 1) : 1;
    scale.push([t0, colors[i]!], [t1, colors[i]!]);
  }
  return scale;
}

export function makeBoxPlot(options: InspectionPlotOptions) {
  const { dataframes, plotTitle = "", xAxisLabel = "", tracePrefix = "" } = options;
  let featureCount = 0;
  let splitCountForScale = 0;
  const traces = [];
  for (const [i, df] of dataframes.entries()) {
    let featureNames = df.index.map((a) =>
      typeof a === "string" ? a : (a as [unknown, string])[1]
    );
    featureCount = featureNames.length;
    if (featureNames.every((_, idx) => featureNames[idx] === undefined)) {
      featureNames = Array.from({ length: df.index.length }, (_, idx) => `Feature #${idx}`);
    }
    const x = df.data.flat();
    const yNumeric = featureNames.flatMap((_, idx) =>
      Array(df.data[idx]?.length ?? 0).fill(idx)
    ) as number[];
    const hasSplitIndices = df.pointSplitIndices != null && df.pointSplitIndices.length > 0;
    const flatSplitIndices = hasSplitIndices ? df.pointSplitIndices!.flat() : null;
    const splitCount = flatSplitIndices != null ? Math.max(...flatSplitIndices, -1) + 1 : 0;
    const useSplitColors = hasSplitIndices && splitCount > 0;

    const splitCustomdata =
      hasSplitIndices &&
      featureNames.flatMap((name, idx) =>
        (df.pointSplitIndices![idx] ?? []).map((s) => [name, `Split ${s}`] as [string, string])
      );
    const customdata =
      splitCustomdata ??
      featureNames.flatMap((name, idx) => Array(df.data[idx]?.length ?? 0).fill(name));

    if (useSplitColors && splitCountForScale === 0) splitCountForScale = splitCount;

    const JITTER = 0.35;
    const boxPoints = useSplitColors ? false : "all";

    traces.push({
      x,
      y: yNumeric,
      customdata,
      type: "box",
      boxpoints: boxPoints,
      ...(boxPoints === "all" && { jitter: JITTER, pointpos: 0 }),
      fillcolor: "rgba(0, 0, 0, 0)",
      line: { color: "#000000", width: 1 },
      marker: { size: 6, color: "rgba(66, 133, 244, 0.5)", line: { width: 0 } },
      orientation: "h",
      name: tracePrefix ? `${tracePrefix}#${i}` : undefined,
      showlegend: !useSplitColors,
      hovertemplate: hasSplitIndices
        ? "<b>%{customdata[0]}</b><br>Split: %{customdata[1]}<br>Value: %{x}<extra></extra>"
        : "<b>%{customdata}</b><br>Value: %{x}<extra></extra>",
    });

    if (useSplitColors) {
      const yJittered = yNumeric.map((yi, idx) => {
        const start = featureNames.reduce(
          (acc, _, fi) => (fi < yi ? acc + (df.data[fi]?.length ?? 0) : acc),
          0
        );
        const pointInFeature = idx - start;
        const nInFeature = df.data[yi]?.length ?? 1;
        const t = (pointInFeature + 1) / (nInFeature + 1);
        return yi + t * 2 * JITTER - JITTER;
      });
      traces.push({
        x,
        y: yJittered,
        customdata,
        type: "scatter",
        mode: "markers",
        marker: {
          size: 6,
          color: flatSplitIndices!,
          coloraxis: SPLIT_COLORAXIS_ID,
          line: { width: 0 },
          symbol: "circle",
        },
        hovertemplate:
          "<b>%{customdata[0]}</b><br>Split: %{customdata[1]}<br>Value: %{x}<extra></extra>",
        showlegend: false,
      });
    }
  }

  if (splitCountForScale > 0) {
    const splitColors = getSplitPaletteColors(splitCountForScale);
    traces.push(
      ...splitColors.map((color, s) => ({
        x: [null],
        y: [null],
        type: "scatter" as const,
        mode: "markers" as const,
        marker: { size: 10, color, symbol: "circle" as const, line: { width: 0 } },
        name: `Split ${s}`,
        showlegend: true,
        legendgroup: "splits",
        hoverinfo: "skip" as const,
      }))
    );
  }

  const shapes: Shape[] = Array.from(
    { length: featureCount },
    (_, i) =>
      ({
        type: "rect",
        xref: "paper",
        yref: "y",
        x0: 0,
        x1: 1,
        y0: -0.5 + i,
        y1: 0.5 + i,
        fillcolor: i % 2 === 0 ? "rgba(0, 0, 0, 0.03)" : "rgba(0, 0, 0, 0)",
        line: { width: 0 },
      }) as Shape
  );
  const allFeatureNames = dataframes.flatMap((df) =>
    df.index.map((a) => (typeof a === "string" ? a : (a as [unknown, string])[1]))
  );
  const maxLabelLen = Math.max(MAX_LABEL_LENGTH, ...allFeatureNames.map((n) => String(n).length));
  const leftMargin = maxLabelLen * 16 * 0.6 + 10;

  const layout: PlotlyWidgetSpec["layout"] = {
    shapes,
    margin: { t: 30, l: leftMargin, b: 50, r: 0 },
    showlegend: dataframes.length > 1 || splitCountForScale > 0,
    ...(splitCountForScale > 0 && {
      [SPLIT_COLORAXIS_ID]: {
        colorscale: makeDiscreteColorscale(splitCountForScale),
        cmin: 0,
        cmax: Math.max(0, splitCountForScale - 1),
        showscale: false,
      },
    }),
    xaxis: {
      showgrid: false,
      zeroline: true,
      title: {
        text: xAxisLabel,
        font: {
          family: "GeistMono, monospace",
          size: 14,
          color: "#2f3037",
        },
      },
    },
    yaxis: {
      dtick: 1,
      tickvals: Array.from({ length: featureCount }, (_, k) => k),
      ticktext: allFeatureNames.slice(0, featureCount),
      showgrid: true,
      tickfont: {
        family: "GeistMono, monospace",
        size: 16,
        color: "#2f3037",
      },
    },
    title: {
      text: plotTitle,
    },
    boxmode: dataframes.length > 1 ? "group" : undefined,
  };

  return {
    data: traces,
    layout,
    frames: [],
  } as PlotlyWidgetSpec;
}

export function makeBarplot(options: InspectionPlotOptions) {
  const { dataframes, plotTitle = "", tracePrefix = "", xAxisLabel = "", legendTitle } = options;
  const traces = [];
  let maxLabelLen = MAX_LABEL_LENGTH;
  const useColors = dataframes.length > 1 && dataframes.some((df) => df.traceName != null);
  for (const [i, df] of dataframes.entries()) {
    const featureNames = df.index.map((idx) => idx.toString());
    maxLabelLen = Math.max(maxLabelLen, ...featureNames.map((n) => n.length));
    traces.push({
      x: df.data.flat(),
      y: featureNames,
      customdata: featureNames,
      type: "bar",
      orientation: "h",
      name: df.traceName ?? (tracePrefix ? `${tracePrefix}#${i}` : undefined),
      hovertemplate: "<b>%{customdata}</b><br>Value: %{x}<extra></extra>",
    });
  }
  const shapes: Shape[] = [];
  const first = traces[0];
  if (first && Array.isArray(first.x)) {
    for (let i = 0; i < (first.x as number[]).length; i++) {
      shapes.push({
        type: "rect",
        xref: "paper",
        yref: "y",
        x0: 0,
        x1: 1,
        y0: -0.5 + i,
        y1: 0.5 + i,
        fillcolor: i % 2 === 0 ? "rgba(0, 0, 0, 0.03)" : "rgba(0, 0, 0, 0)",
        line: { width: 0 },
      } as Shape);
    }
  }
  const leftMargin = maxLabelLen * 16 * 0.6 + 10;
  return {
    data: traces,
    layout: {
      shapes,
      margin: { t: 30, l: leftMargin, b: 50, r: 35 },
      showlegend: useColors,
      ...(useColors && legendTitle ? { legend: { title: { text: legendTitle } } } : {}),
      xaxis: {
        title: {
          text: xAxisLabel,
          font: {
            family: "Geist, sans-serif",
            size: 14,
            color: "#2f3037",
          },
        },
      },
      yaxis: {
        dtick: 1,
        tickfont: {
          family: "GeistMono, monospace",
          size: 16,
          color: "#2f3037",
        },
      },
      title: {
        text: plotTitle,
      },
      barmode: useColors ? ("group" as const) : undefined,
    },
    frames: [],
  } as PlotlyWidgetSpec;
}
